/**
 * Updates J-code documents for a single order via Postgres.
 * @module updateJCodeDocuments
 */

import { JCodeDocumentUpdatePayload } from "../../../../medonc/components/modals/narLetterModal/utils/generateDocumentUpdatePayloads";
import { API_ENDPOINTS } from "../../../enums/apiUrls";
import { getBearerToken } from "../../postCall/bearerToken";
import { throwApiError } from "../../throwApiError";

/** Response shape returned by the J-code documents update endpoint. */
export interface UpdateJCodeDocumentsResponse {
  status: string;
  message: string;
}

/**
 * Sends a POST request to update documents for a specific J-code.
 *
 * Failures follow the `/prior-auth-utilities/*` HTTP-status contract: 4xx/5xx
 * responses throw an `Error` enriched with `status` and `detail` (read by
 * `utils/showApiError` on the consumer side).
 *
 * @param params - Payload containing order_id, org_id, j_code_id, and documents.
 * @param signal - Optional AbortSignal to cancel the in-flight request.
 * @returns The parsed JSON response on success.
 * @throws {Error & { status: number; detail?: string }} On HTTP failure.
 * @throws Re-throws any network or parsing error after logging (except AbortError).
 */
export const updateJCodeDocuments = async (
  params: JCodeDocumentUpdatePayload,
  signal?: AbortSignal,
): Promise<UpdateJCodeDocumentsResponse> => {
  try {
    const url = API_ENDPOINTS.UPDATE_J_CODE_DOCUMENTS;
    const bearerToken = await getBearerToken();
    const raw = JSON.stringify({
      order_id: params.order_id,
      org_id: params.org_id,
      j_code_id: params.j_code_id,
      documents: params.documents,
    });
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearerToken}`,
      },
      body: raw,
      ...(signal && { signal }),
    });
    if (!response.ok) {
      await throwApiError(response);
    }
    const result: UpdateJCodeDocumentsResponse = await response.json();
    return result;
  } catch (error: any) {
    if (error.name !== "AbortError") {
      console.error(error);
    }
    throw error;
  }
};

/**
 * Result of running all sequential J-code document updates.
 *
 * Under the HTTP-status contract, failures throw and never resolve to a
 * result, so `success` is always `true` when this value is observable.
 */
export interface UpdateAllJCodeDocumentsResult {
  /** Always `true` when the promise resolves; failures throw instead. */
  success: boolean;
  /** Individual results in the same order as the input payloads. */
  results: UpdateJCodeDocumentsResponse[];
}

/**
 * Executes J-code document updates sequentially, one after another.
 *
 * Failures now surface as thrown errors from {@link updateJCodeDocuments}
 * (HTTP-status driven contract); the loop stops at the first throw and
 * propagates it to the caller. Aborts are checked between calls so
 * cancellation takes effect between requests as well as during them.
 *
 * @param payloads - Array of per-J-code update payloads.
 * @param signal - AbortSignal shared across all requests; aborting it
 *   cancels the current in-flight request and skips remaining ones.
 * @returns Aggregate result; `success` is always `true` when this function
 *   resolves (failures throw instead).
 */
export const updateAllJCodeDocuments = async (
  payloads: JCodeDocumentUpdatePayload[],
  signal?: AbortSignal,
): Promise<UpdateAllJCodeDocumentsResult> => {
  const results: UpdateJCodeDocumentsResponse[] = [];

  for (const payload of payloads) {
    if (signal?.aborted) {
      throw new DOMException("Aborted", "AbortError");
    }

    const result = await updateJCodeDocuments(payload, signal);
    results.push(result);
  }

  return { success: true, results };
};
